import type { OsWindow, OsWorkspace } from "./OsContext";
import { clampWindowsToDesktopBoundsList } from "./clampWindowGeometry";
import {
  applyReopenParamEvents,
  findExistingWindowForOpen,
  shouldBumpFocusNonce,
} from "./windowKey";

export const DEFAULT_NEXT_Z_INDEX = 100;

const ACE_TASK_REVIEW_APP_ID = "ops-ace-task-review";

export interface OsWindowReducerState {
  windows: OsWindow[];
  workspaces: OsWorkspace[];
  activeWorkspaceId: string;
  nextZIndex: number;
}

export type OpenWindowActionPayload = {
  appId: string;
  title: string;
  params?: Record<string, string>;
  focus?: boolean;
  demoteWindowId?: string;
  stackBelowWindowId?: string;
  newWindow?: boolean;
  replaceParams?: boolean;
  newWindowId: string;
  minSize?: { width: number; height: number };
  workspaceId: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  isMaximized: boolean;
  demoteMaximizedOnFocus?: boolean;
};

export type OsWindowAction =
  | { type: "hydrate"; state: OsWindowReducerState }
  | { type: "open"; payload: OpenWindowActionPayload }
  | { type: "focus"; windowId: string }
  | { type: "close"; windowId: string }
  | { type: "closeAll" }
  | { type: "update"; windowId: string; patch: Partial<OsWindow> }
  | {
      type: "clamp";
      desktopWidth: number;
      windowedDesktopHeight: number;
      maximizedDesktopHeight?: number;
    }
  | {
      type: "adjustForResize";
      newDesktopWidth: number;
      oldDesktopWidth: number;
    }
  | { type: "addWorkspace"; workspace: OsWorkspace }
  | { type: "removeWorkspace"; workspaceId: string }
  | { type: "switchWorkspace"; workspaceId: string }
  | { type: "updateWorkspaceName"; workspaceId: string; name: string }
  | { type: "updateWorkspaceTheme"; workspaceId: string; themeSeed: string }

export function dedupeAceTaskReviewWindows<T extends { id: string; appId: string; zIndex: number }>(
  windows: T[],
): T[] {
  const aceWindows = windows.filter((w) => w.appId === ACE_TASK_REVIEW_APP_ID);
  if (aceWindows.length <= 1) {
    return windows;
  }

  const keep = aceWindows.reduce((best, w) => (w.zIndex >= best.zIndex ? w : best));
  const dropIds = new Set(aceWindows.filter((w) => w.id !== keep.id).map((w) => w.id));
  return windows.filter((w) => !dropIds.has(w.id));
}

export function visibleWindowsInWorkspace(
  windows: readonly OsWindow[],
  workspaceId: string,
): OsWindow[] {
  return windows.filter(
    (w) => w.workspaceId === workspaceId && !w.isMinimized,
  );
}

export function topVisibleWindowInWorkspace(
  windows: readonly OsWindow[],
  workspaceId: string,
): OsWindow | undefined {
  const visible = visibleWindowsInWorkspace(windows, workspaceId);
  if (visible.length === 0) return undefined;
  return visible.reduce((top, w) => (w.zIndex >= top.zIndex ? w : top));
}

function maxVisibleZIndexInWorkspace(
  windows: readonly OsWindow[],
  workspaceId: string,
): number {
  const visible = visibleWindowsInWorkspace(windows, workspaceId);
  if (visible.length === 0) return DEFAULT_NEXT_Z_INDEX - 1;
  return Math.max(...visible.map((w) => w.zIndex));
}

export function renumberZIndices(
  windows: readonly OsWindow[],
): { windows: OsWindow[]; nextZIndex: number } {
  if (windows.length === 0) {
    return { windows: [], nextZIndex: DEFAULT_NEXT_Z_INDEX };
  }

  const order = [...windows].sort(
    (a, b) => a.zIndex - b.zIndex || a.id.localeCompare(b.id),
  );
  const zById = new Map<string, number>();
  order.forEach((w, i) => {
    zById.set(w.id, DEFAULT_NEXT_Z_INDEX + i);
  });

  let changed = false;
  const next = windows.map((w) => {
    const newZ = zById.get(w.id)!;
    if (w.zIndex === newZ) {
      return w;
    }
    changed = true;
    return { ...w, zIndex: newZ };
  });

  const maxZ = DEFAULT_NEXT_Z_INDEX + windows.length - 1;
  return {
    windows: changed ? next : (windows as OsWindow[]),
    nextZIndex: maxZ + 1,
  };
}

function sortWindowsByZ(windows: readonly OsWindow[]): OsWindow[] {
  return [...windows].sort(
    (a, b) => a.zIndex - b.zIndex || a.id.localeCompare(b.id),
  );
}

function applyStackOrder(
  windows: readonly OsWindow[],
  orderedIds: string[],
): OsWindow[] {
  const zById = new Map<string, number>();
  orderedIds.forEach((id, i) => {
    zById.set(id, DEFAULT_NEXT_Z_INDEX + i);
  });

  let changed = false;
  const next = windows.map((w) => {
    const newZ = zById.get(w.id);
    if (newZ === undefined || w.zIndex === newZ) {
      return w;
    }
    changed = true;
    return { ...w, zIndex: newZ };
  });

  return changed ? next : (windows as OsWindow[]);
}

function stackOrderIds(windows: readonly OsWindow[]): string[] {
  return sortWindowsByZ(windows).map((w) => w.id);
}

function insertWindowInStack(
  windows: readonly OsWindow[],
  windowId: string,
  insertBelowWindowId: string | null,
  activeWorkspaceId: string,
): OsWindow[] {
  const ids = stackOrderIds(windows);
  const without = ids.filter((id) => id !== windowId);
  let insertIndex = without.length;

  if (insertBelowWindowId) {
    const anchorIndex = without.indexOf(insertBelowWindowId);
    if (anchorIndex >= 0) {
      insertIndex = anchorIndex;
    }
  } else {
    const top = topVisibleWindowInWorkspace(windows, activeWorkspaceId);
    if (top) {
      const topIndex = without.indexOf(top.id);
      if (topIndex >= 0) {
        insertIndex = topIndex;
      }
    }
  }

  without.splice(insertIndex, 0, windowId);
  return applyStackOrder(windows, without);
}

function raiseWindowToTop(
  windows: readonly OsWindow[],
  windowId: string,
): OsWindow[] {
  const ids = stackOrderIds(windows).filter((id) => id !== windowId);
  ids.push(windowId);
  return applyStackOrder(windows, ids);
}

function isIncomingParamsSubset(
  incoming: Record<string, string>,
  existing: Record<string, string>,
): boolean {
  if (Object.keys(incoming).length === 0) {
    return false;
  }
  return Object.entries(incoming).every(([key, value]) => existing[key] === value);
}

function focusNoncePatch(
  window: OsWindow,
  nextParams: Record<string, string>,
  paramsProvided: boolean,
): Pick<OsWindow, "focusNonce"> | Record<string, never> {
  if (!paramsProvided || !shouldBumpFocusNonce(nextParams)) {
    return {};
  }
  return { focusNonce: Date.now() };
}

function demoteMaximizedWindowsInWorkspace(
  windows: OsWindow[],
  workspaceId: string,
  exceptWindowId?: string,
): OsWindow[] {
  let changed = false;
  const next = windows.map((w) => {
    if (
      w.workspaceId === workspaceId &&
      w.isMaximized &&
      w.id !== exceptWindowId
    ) {
      changed = true;
      return { ...w, isMaximized: false, isMinimized: true };
    }
    return w;
  });
  return changed ? next : windows;
}

function patchWindow(
  windows: readonly OsWindow[],
  windowId: string,
  patch: Partial<OsWindow>,
): OsWindow[] | null {
  const target = windows.find((w) => w.id === windowId);
  if (!target) {
    return null;
  }

  const hasChanges = Object.keys(patch).some((key) => {
    const k = key as keyof OsWindow;
    const oldVal = target[k];
    const newVal = patch[k];
    if (
      typeof oldVal === "object" &&
      typeof newVal === "object" &&
      oldVal !== null &&
      newVal !== null
    ) {
      return JSON.stringify(oldVal) !== JSON.stringify(newVal);
    }
    return oldVal !== newVal;
  });

  if (!hasChanges) {
    return null;
  }

  return windows.map((w) => (w.id === windowId ? { ...w, ...patch } : w));
}

function resolveReuseParams(
  appId: string,
  existing: OsWindow,
  openParams: Record<string, string>,
  paramsProvided: boolean,
  replaceParams: boolean,
): Record<string, string> {
  if (!paramsProvided) {
    return existing.params;
  }
  if (replaceParams) {
    return applyReopenParamEvents(appId, openParams);
  }
  if (isIncomingParamsSubset(openParams, existing.params)) {
    return existing.params;
  }
  return applyReopenParamEvents(appId, openParams);
}

export function osWindowReducer(
  state: OsWindowReducerState,
  action: OsWindowAction,
): OsWindowReducerState {
  switch (action.type) {
    case "hydrate":
      return action.state;

    case "closeAll":
      if (state.windows.length === 0 && state.nextZIndex === DEFAULT_NEXT_Z_INDEX) {
        return state;
      }
      return {
        ...state,
        windows: [],
        nextZIndex: DEFAULT_NEXT_Z_INDEX,
      };

    case "close": {
      const remaining = state.windows.filter((w) => w.id !== action.windowId);
      if (remaining.length === state.windows.length) {
        return state;
      }
      const { windows, nextZIndex } = renumberZIndices(remaining);
      return {
        ...state,
        windows,
        nextZIndex: remaining.length === 0 ? DEFAULT_NEXT_Z_INDEX : nextZIndex,
      };
    }

    case "focus": {
      const target = state.windows.find((w) => w.id === action.windowId);
      if (!target) {
        return state;
      }

      const workspaceMax = maxVisibleZIndexInWorkspace(
        state.windows,
        target.workspaceId,
      );
      const alreadyFocused =
        target.workspaceId === state.activeWorkspaceId &&
        !target.isMinimized &&
        target.zIndex >= workspaceMax;

      if (alreadyFocused) {
        return state;
      }

      let windows = state.windows.map((w) =>
        w.id === action.windowId ? { ...w, isMinimized: false } : w,
      );
      windows = raiseWindowToTop(windows, action.windowId);
      const { windows: renumbered, nextZIndex } = renumberZIndices(windows);

      return {
        ...state,
        windows: renumbered,
        nextZIndex,
        activeWorkspaceId: target.workspaceId,
      };
    }

    case "open": {
      const {
        appId,
        title,
        params,
        focus = true,
        demoteWindowId,
        stackBelowWindowId,
        newWindow,
        replaceParams = false,
        newWindowId,
        minSize,
        workspaceId,
        position,
        size,
        isMaximized,
        demoteMaximizedOnFocus,
      } = action.payload;

      const paramsProvided = params !== undefined;
      const openParams = params ?? {};
      const shouldRaise = focus !== false && !stackBelowWindowId;

      const existingWindow =
        newWindow
          ? undefined
          : findExistingWindowForOpen(
              state.windows,
              appId,
              openParams,
              state.activeWorkspaceId,
            );

      if (existingWindow) {
        let windows = state.windows;
        let activeWorkspaceId = state.activeWorkspaceId;

        if (existingWindow.workspaceId !== activeWorkspaceId) {
          activeWorkspaceId = existingWindow.workspaceId;
        }

        if (
          demoteMaximizedOnFocus &&
          !existingWindow.isMaximized &&
          !isMaximized
        ) {
          const demoted = demoteMaximizedWindowsInWorkspace(
            windows,
            activeWorkspaceId,
            existingWindow.id,
          );
          if (demoted !== windows) {
            windows = demoted;
          }
        }

        const nextParams = resolveReuseParams(
          appId,
          existingWindow,
          openParams,
          paramsProvided,
          replaceParams,
        );
        const paramsChanged =
          JSON.stringify(nextParams) !== JSON.stringify(existingWindow.params);
        const titleChanged = Boolean(title) && title !== existingWindow.title;
        const needsUnminimize = existingWindow.isMinimized;
        const focusPatch = focusNoncePatch(
          existingWindow,
          paramsProvided ? openParams : existingWindow.params,
          paramsProvided,
        );
        const focusNonceChanged =
          focusPatch.focusNonce !== undefined &&
          focusPatch.focusNonce !== existingWindow.focusNonce;

        const needsPatch =
          paramsChanged ||
          titleChanged ||
          needsUnminimize ||
          focusNonceChanged;

        if (needsPatch) {
          windows = windows.map((w) =>
            w.id === existingWindow.id
              ? {
                  ...w,
                  ...(paramsChanged ? { params: nextParams } : {}),
                  ...focusPatch,
                  ...(titleChanged ? { title } : {}),
                  isMinimized: false,
                }
              : w,
          );
        }

        if (shouldRaise) {
          windows = raiseWindowToTop(windows, existingWindow.id);
        }

        const { windows: renumbered, nextZIndex } = renumberZIndices(windows);

        if (
          activeWorkspaceId === state.activeWorkspaceId &&
          !needsPatch &&
          !needsUnminimize &&
          !shouldRaise &&
          renumbered.length === state.windows.length &&
          renumbered.every((w, i) => w === state.windows[i])
        ) {
          return state;
        }

        return {
          ...state,
          windows: renumbered,
          nextZIndex,
          activeWorkspaceId,
        };
      }

      let windows = state.windows;

      if (demoteWindowId) {
        windows = windows.map((w) =>
          w.id === demoteWindowId
            ? { ...w, isMaximized: false, isMinimized: true }
            : w,
        );
      }
      if (appId === "task-review" && !isMaximized) {
        const demoted = demoteMaximizedWindowsInWorkspace(
          windows,
          workspaceId,
        );
        if (demoted !== windows) {
          windows = demoted;
        }
      }

      const initialParams = applyReopenParamEvents(appId, openParams);
      const newWindowObj: OsWindow = {
        id: newWindowId,
        appId,
        title,
        params: initialParams,
        ...(shouldBumpFocusNonce(initialParams) ? { focusNonce: Date.now() } : {}),
        workspaceId,
        position,
        size,
        minSize,
        zIndex: DEFAULT_NEXT_Z_INDEX,
        isMinimized: false,
        isMaximized,
      };

      windows = [...windows, newWindowObj];

      if (shouldRaise) {
        windows = raiseWindowToTop(windows, newWindowId);
      } else if (stackBelowWindowId) {
        windows = insertWindowInStack(
          windows,
          newWindowId,
          stackBelowWindowId,
          workspaceId,
        );
      } else if (focus === false) {
        windows = insertWindowInStack(
          windows,
          newWindowId,
          null,
          workspaceId,
        );
      }

      const { windows: renumbered, nextZIndex } = renumberZIndices(windows);

      return {
        ...state,
        windows: renumbered,
        nextZIndex,
      };
    }

    case "update": {
      const patched = patchWindow(state.windows, action.windowId, action.patch);
      if (!patched) {
        return state;
      }
      return { ...state, windows: patched };
    }

    case "adjustForResize": {
      const { newDesktopWidth, oldDesktopWidth } = action;
      const isShrinking = newDesktopWidth < oldDesktopWidth;
      const isExpanding = newDesktopWidth > oldDesktopWidth;

      let changed = false;
      const next = state.windows.map((window) => {
        if (window.isMinimized || window.isMaximized) {
          return window;
        }

        if (isShrinking) {
          const windowRightEdge = window.position.x + window.size.width;
          const wouldBeCutOff = windowRightEdge > newDesktopWidth;

          if (wouldBeCutOff) {
            changed = true;
            const preAdjustmentPosition =
              window.preAdjustmentPosition || { ...window.position };
            const distanceFromRightEdge = oldDesktopWidth - windowRightEdge;
            const newRightEdge = newDesktopWidth - distanceFromRightEdge;
            const newX = Math.round(Math.max(0, newRightEdge - window.size.width));

            return {
              ...window,
              position: { x: newX, y: window.position.y },
              preAdjustmentPosition,
              adjustedPosition: { x: newX, y: window.position.y },
            };
          }
        }

        if (isExpanding && window.preAdjustmentPosition && window.adjustedPosition) {
          const wasNotManuallyMoved =
            Math.abs(window.position.x - window.adjustedPosition.x) < 5;

          changed = true;
          if (wasNotManuallyMoved) {
            return {
              ...window,
              position: { ...window.preAdjustmentPosition },
              preAdjustmentPosition: undefined,
              adjustedPosition: undefined,
            };
          }
          return {
            ...window,
            preAdjustmentPosition: undefined,
            adjustedPosition: undefined,
          };
        }

        return window;
      });

      if (!changed) {
        return state;
      }
      return { ...state, windows: next };
    }

    case "clamp": {
      const { desktopWidth, windowedDesktopHeight, maximizedDesktopHeight } =
        action;
      if (desktopWidth <= 0 || windowedDesktopHeight <= 0) {
        return state;
      }

      const { windows: nextWindows, changedWindowIds } =
        clampWindowsToDesktopBoundsList(
          state.windows,
          desktopWidth,
          windowedDesktopHeight,
          {
            maximizedDesktopHeight:
              maximizedDesktopHeight ?? windowedDesktopHeight,
          },
        );

      if (changedWindowIds.length === 0) {
        return state;
      }
      return { ...state, windows: nextWindows };
    }

    case "addWorkspace":
      return {
        ...state,
        workspaces: [...state.workspaces, action.workspace],
        activeWorkspaceId: action.workspace.id,
      };

    case "removeWorkspace": {
      if (state.workspaces.length <= 1) {
        return state;
      }
      const workspaces = state.workspaces.filter(
        (ws) => ws.id !== action.workspaceId,
      );
      const windows = state.windows.filter(
        (w) => w.workspaceId !== action.workspaceId,
      );
      let activeWorkspaceId = state.activeWorkspaceId;
      if (activeWorkspaceId === action.workspaceId) {
        activeWorkspaceId = workspaces[0]?.id ?? activeWorkspaceId;
      }
      const { windows: renumbered, nextZIndex } = renumberZIndices(windows);
      return {
        ...state,
        workspaces,
        windows: renumbered,
        activeWorkspaceId,
        nextZIndex: windows.length === 0 ? DEFAULT_NEXT_Z_INDEX : nextZIndex,
      };
    }

    case "switchWorkspace":
      if (state.activeWorkspaceId === action.workspaceId) {
        return state;
      }
      return { ...state, activeWorkspaceId: action.workspaceId };

    case "updateWorkspaceName":
      return {
        ...state,
        workspaces: state.workspaces.map((ws) =>
          ws.id === action.workspaceId ? { ...ws, name: action.name } : ws,
        ),
      };

    case "updateWorkspaceTheme":
      return {
        ...state,
        workspaces: state.workspaces.map((ws) =>
          ws.id === action.workspaceId ? { ...ws, themeSeed: action.themeSeed } : ws,
        ),
      };

    default:
      return state;
  }
}
